import React, { useState, useMemo } from 'react';
import { Button } from '@comfama/comfama-ui-react';
import { DataTable } from '@/components/organisms/data-table/DataTable';
import GenericToast from '@/components/atoms/generic-toast/genericToast';
import Loader from '@/components/atoms/loader/loader';
import { formatCurrency } from '@/utils/libranzasFormat';
import './libranzasHistorial.scss';

const BASE_COLUMNS = [
    { accessorKey: 'fechaPago', header: 'Fecha' },
    { accessorKey: 'referenciaTransaccion', header: 'Referencia' },
    { accessorKey: 'obligacion', header: 'Obligación' },
    {
        accessorKey: 'autorizacionCus',
        header: 'Autorización',
        cell: ({ row }: any) => row.autorizacionCus ?? '-',
    },
    { accessorKey: 'estado', header: 'Estado' },
    {
        accessorKey: 'valor',
        header: 'Valor',
        cell: ({ row }: any) => formatCurrency(String(row.valor ?? 0)),
    },
];

interface LibranzasHistorialProps {
    rows: Record<string, any>[];
    loading: boolean;
    notFound?: boolean;
    showDownloadButton?: boolean;
    onDownloadTransaction?: (transactionId: string | number) => Promise<void>;
}

function LibranzasHistorial({ rows, loading, notFound, showDownloadButton, onDownloadTransaction }: LibranzasHistorialProps) {
    const [downloadingId, setDownloadingId] = useState<string | number | null>(null);
    const [downloadToast, setDownloadToast] = useState<React.ReactNode>(null);

    const handleDownloadTransaction = async (transactionId: string | number) => {
        if (!onDownloadTransaction) return;
        setDownloadingId(transactionId);
        try {
            await onDownloadTransaction(transactionId);
            setDownloadToast(
                <GenericToast
                    setActiveToast={setDownloadToast}
                    isOk
                    message="El archivo se descargó correctamente."
                    position="up-right"
                />
            );
        } catch {
            setDownloadToast(
                <GenericToast
                    setActiveToast={setDownloadToast}
                    isOk={false}
                    message="Ocurrió un error al descargar el archivo. Por favor intente de nuevo."
                    position="up-right"
                />
            );
        } finally {
            setDownloadingId(null);
        }
    };

    const historialColumns = useMemo(() => {
        const cols = [...BASE_COLUMNS];
        if (showDownloadButton) {
            cols.push({
                accessorKey: 'descargar',
                header: 'Descargar',
                cell: ({ row }: any) => (
                    <div className="o-libranzas-historial__descargar-cell">
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleDownloadTransaction(row.obligacion)}
                            disabled={downloadingId === row.obligacion || loading}
                            className="o-libranzas-historial__btn-descargar"
                            aria-label={downloadingId === row.obligacion ? 'Descargando archivo' : undefined}
                        >
                            {downloadingId === row.obligacion ? (
                                <Loader size="__small" />
                            ) : (
                                'Excel'
                            )}
                        </Button>
                    </div>
                ),
            });
        }
        return cols;
    }, [showDownloadButton, downloadingId, loading]);

    if (notFound) {
        return (
            <div className="o-libranzas-historial">
                <p className="o-libranzas-historial__not-found">
                    No se encontraron transacciones para los filtros indicados.
                </p>
            </div>
        );
    }

    return (
        <div className="o-libranzas-historial">
            <DataTable data={rows} head={historialColumns} loading={loading} />
            {downloadToast}
        </div>
    );
}

export default LibranzasHistorial;
